import React, { useRef, useState, useCallback } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { MdArrowBackIosNew, MdArrowForwardIos } from 'react-icons/md';
import { useWindowSize } from 'usehooks-ts';

import { useElasticSearchKeyCategories } from '@/modules/core/hooks/useElasticSearchServer';

const items = [
  {
    id: '66c34239d16d7f3b0c2ec56e',
    link: 'immoneuf',
    linkFr: 'immoneuf',
    DesktopImage: '/media/key-desktop.webp',
    label: 'ImmoNeuf',
    circle:
      'bg-[#065797]/50 hover:translate-x-0 hover:translate-y-3 right-7 top-8',
    dimensions: 'w-20 md:w-32 lg:w-46',
  },

  {
    id: '60be84bc50ab95b45b08a093', // Immobilier
    link: 'real-estate',
    linkFr: 'immobilier',
    DesktopImage: '/media/realestate-carousel-desktop.webp',
    label: 'Immobilier',
    circle:
      'bg-[#39B889]/50 hover:translate-x-3 hover:translate-y-0 top-8 md:top-10 right-5  md:right-8',
    dimensions: 'w-20 md:w-32 lg:w-46',
  },
  {
    id: '60be84bc50ab95b45b08a094', // Véhicules
    link: 'véhicules',
    linkFr: 'véhicules',
    DesktopImage: '/media/cars-carousel-desktop.webp',
    label: 'Véhicules',
    circle:
      'bg-[#FDC305]/50 hover:translate-x-3 hover:translate-y-3 top-7 left-4',
    dimensions: 'w-20 md:w-32 lg:w-fit',
  },
  {
    id: '60be84bc50ab95b45b08a097', // Multimédias
    link: 'tech',
    linkFr: 'informatique-et-multimedias',
    DesktopImage: '/media/multimedia-carousel-desktop.webp',
    label: 'Informatique et Multimedias',
    circle:
      'bg-[#39DEEE]/50 hover:translate-x-3 hover:translate-y-3 top-6 left-10 lg:left-8',
    dimensions: 'w-20 md:w-32 lg:w-fit',
  },
  {
    id: '60be84bc50ab95b45b08a095', // Maison & jardin
    link: 'home-and-garden',
    linkFr: 'pour-la-maison-et-jardin',
    DesktopImage: '/media/home-carousel-desktop.webp',
    label: 'Maison et Jardin',
    circle: 'bg-[#CF4032]/50 hover:translate-x-0 hover:translate-y-3 top-5',
    dimensions: 'w-20 md:w-32 lg:w-fit',
  },
  {
    id: '60be84bc50ab95b45b08a096', // Loisirs
    link: 'leisure-and-entertainment',
    linkFr: 'loisirs-et-divertissement',
    DesktopImage: '/media/loisir-carousel-desktop.webp',
    label: 'Loisirs et Divertissement',
    circle:
      'bg-[#FADBD3]/50 hover:-translate-x-3 hover:translate-y-3 top-6 right-6',
    dimensions: 'w-20 md:w-32 lg:w-fit',
  },
  {
    id: '60be84bc50ab95b45b08a099', // Habillement
    link: 'clothing-and-well-being',
    linkFr: 'habillement-et-bien-etre',
    DesktopImage: '/media/clothes-carousel-desktop.webp',
    label: 'Habillement et Bien Etre',
    circle:
      'bg-[#404EBB]/50 hover:translate-x-0 hover:translate-y-3 top-6 left-6',
    dimensions: 'w-20 md:w-32 lg:w-fit',
  },
  {
    id: '60be84bc50ab95b45b08a098', // Emplois
    link: 'jobs-and-services',
    linkFr: 'emploi-et-services',
    DesktopImage: '/media/jobs-carousel-desktop.webp',
    label: 'Emploi et Services',
    circle:
      'bg-[#979595]/50 hover:translate-x-3 hover:translate-y-3 top-10 left-4',
    dimensions: 'w-20 md:w-32 lg:w-fit',
  },
  {
    id: '60be84bd50ab95b45b08a09a', // Entreprises
    link: 'Entreprises',
    linkFr: 'entreprises',
    DesktopImage: '/media/entreprise-carousel-desktop.webp',
    label: 'Entreprises',
    circle:
      'bg-[#F48D83]/50 hover:translate-x-0 hover:translate-y-3 top-2 left-6',
    dimensions: 'w-20 md:w-32 lg:w-fit',
  },
];

const CategoriesCarousel = React.memo(() => {
  const [handleCategoryClick] = useElasticSearchKeyCategories();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showPrevButton, setShowPrevButton] = useState(false);
  const [showNextButton, setShowNextButton] = useState(true);
  const { width: windowWidth } = useWindowSize();

  const handlePrevClick = useCallback(() => {
    if (carouselRef.current) {
      const scrollAmount = windowWidth < 768 ? -150 : -200;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [windowWidth]);

  const handleNextClick = useCallback(() => {
    if (carouselRef.current) {
      const scrollAmount = windowWidth < 768 ? 150 : 200;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [windowWidth]);

  const handleScroll = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      const isEnd = scrollLeft + clientWidth >= scrollWidth - 5; // Small buffer for precision

      setShowPrevButton(scrollLeft > 5);
      setShowNextButton(!isEnd);
    }
  }, []);

  const renderCarouselItem = (item: (typeof items)[0]) => (
    <div
      key={item.id}
      className={`flex flex-col items-center justify-between relative flex-shrink-0  ${item.dimensions}`}
    >
      <Link
        href={{
          pathname: `/ads${item.linkFr ? `/c/${item.linkFr}` : ''}`,
        }}
        replace
        passHref
        className="flex mx-auto justify-center items-end w-full h-12 sm:h-14 md:h-16 lg:h-20 relative"
        onClick={() => {
          if (item.link !== '') {
            handleCategoryClick(item.linkFr);
          }
        }}
        target="_blank"
      >
        <div
          className={`absolute w-4 h-4  md:w-8 md:h-8 lg:w-10 lg:h-10 rounded-full ${item.circle} hover:transition ease-in duration-300 cursor-pointer transition-transform duration-600`}
        />
        <Image
          loading="lazy"
          width={100}
          height={100}
          className="w-full h-full object-contain transition-all duration-300"
          src={item.DesktopImage}
          unoptimized
          alt={`catégorie ${item.label}`}
        />
      </Link>
      <Link
        href={{
          pathname: `/ads${item.linkFr ? `/c/${item.linkFr}` : ''}`,
        }}
        className="w-full text-center mt-2 lg:mt-4 text-xs sm:text-sm md:text-base font-extrabold text-neutral-800 leading-tight px-1"
        replace
        passHref
        onClick={() => {
          if (item.link !== '') {
            handleCategoryClick(item.link);
          }
        }}
        target="_blank"
      >
        <span className="block truncate lg:no-truncate">{item.label}</span>
      </Link>
    </div>
  );

  return (
    <div className="w-full flex flex-col lg:my-4">
      <h2 className="text-base md:text-xl font-extrabold px-2 md:px-0">
        Explorez toutes les catégories
      </h2>

      <div className="w-full justify-center items-center overflow-hidden relative lg:my-2">
        <div
          ref={carouselRef}
          className={`w-full flex items-center gap-x-2 sm:gap-x-3 md:gap-x-4 lg:gap-x-5 overflow-x-auto scrollbar-hide px-2 md:px-0 py-2 ${
            windowWidth < 1700 ? '' : 'justify-center'
          }`}
          onScroll={handleScroll}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map(renderCarouselItem)}
        </div>

        {windowWidth >= 768 && windowWidth < 1700 && (
          <>
            <div className="absolute top-5 -right-1 md:right-0 px-8 md:px-10 h-[120px] md:h-[160px] bg-gradient-to-l from-neutral-50 z-40 pointer-events-none">
              {showNextButton && (
                <button
                  className="absolute top-[35%] transform -translate-y-1/2 right-2 bg-white border border-neutral-400 p-2 md:p-4 rounded-full text-neutral-700 hover:text-neutral-900 focus:outline-none focus:bg-neutral-110 z-50 pointer-events-auto"
                  onClick={handleNextClick}
                >
                  <MdArrowForwardIos size={16} className="md:w-5 md:h-5" />
                </button>
              )}
            </div>
            <div className="absolute top-5 -left-1 px-8 md:px-10 h-[120px] md:h-[160px] bg-gradient-to-r from-neutral-50 z-40 pointer-events-none">
              {showPrevButton && (
                <button
                  className="absolute top-[35%] transform -translate-y-1/2 left-2 bg-white border border-neutral-400 p-2 md:p-4 rounded-full text-neutral-700 hover:text-neutral-900 focus:outline-none focus:bg-neutral-110 z-50 pointer-events-auto"
                  onClick={handlePrevClick}
                >
                  <MdArrowBackIosNew size={16} className="md:w-5 md:h-5" />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});

CategoriesCarousel.displayName = 'CategoriesCarousel';

export default CategoriesCarousel;
